import { Title } from '@components/v2/ui/Title';
import { useOffsetProjectMesurability } from '@features/offsets/hooks/useOffsetProjectMesurability';
import { TMesurabilityProject } from '@features/offsets/types';
import { Box } from '@mantine/core';
import useStore from '@store/index';
import Decimal from 'decimal.js';
import { useTranslation } from 'react-i18next';
import { WarningAlert } from '../WarningAlert/WarningAlert';
import { MeasurabilitiesOffsetCard } from './MeasurabilitiesOffsetCard';
import { MeasurabilitiesOffsetSummary } from './MeasurabilitiesOffsetSummary';
import classes from './MeasurabilitiesOffsetSummaryCart.module.css';

const MeasurabilitiesOffsetSummaryCart = ({
	items,
	onRemove,
}: {
	items: TMesurabilityProject[];
	onRemove: (_project: TMesurabilityProject) => void;
}) => {
	const { t } = useTranslation();
	// Fonction utilitaire pour convertir en nombre sûr
	const safeNumber = (
		value: number | string | undefined | null
	) => {
		const num = Number(value);
		return isNaN(num) ? 0 : num;
	};

	const totalQuantity = items.reduce(
		(acc, item) =>
			acc.plus(
				new Decimal(safeNumber(item.quantitytco2eq))
			),
		new Decimal(0)
	);

	const totalCost = items.reduce(
		(acc, item) =>
			acc.plus(
				new Decimal(safeNumber(item.quantitytco2eq)).mul(
					new Decimal(
						safeNumber(item.sellPrice_europertco2eq)
					)
				)
			),
		new Decimal(0)
	);

	const averagePrice = totalQuantity.gt(0)
		? totalCost.div(totalQuantity)
		: new Decimal(0);

	// Arrondir à 3 chiffres après la virgule
	const roundedQuantity = totalQuantity
		.toDecimalPlaces(3, Decimal.ROUND_HALF_UP)
		.toNumber();
	const roundedCost = totalCost
		.toDecimalPlaces(3, Decimal.ROUND_HALF_UP)
		.toNumber();
	const roundedAveragePrice = averagePrice
		.toDecimalPlaces(3, Decimal.ROUND_HALF_UP)
		.toNumber();

	const { handleOnChangeTco2 } =
		useOffsetProjectMesurability();

	const {
		offsetCreationDatas,
		offsetMesurabilityProjectsSelected,
	} = useStore();

	const isOverrangeQuantity =
		offsetCreationDatas.quantity < totalQuantity;

	const isMaxSelectedProjects =
		Array.isArray(offsetMesurabilityProjectsSelected) &&
		offsetMesurabilityProjectsSelected.length >= 4;

	return (
		<Box className={classes.container}>
			<Title order={6} c="#1D3965">
				{t('global.contributionBreakdown')}
			</Title>

			{items.map((item) => (
				<MeasurabilitiesOffsetCard
					key={item.id}
					contribution={item}
					onQuantityChange={(
						id: string,
						newQuantity: number
					) => {
						handleOnChangeTco2(item, newQuantity);
					}}
					onRemove={onRemove}
					name={item.name ?? '—'}
					quantity={safeNumber(item.quantitytco2eq)}
				/>
			))}

			<MeasurabilitiesOffsetSummary
				totalQuantity={roundedQuantity}
				averagePrice={roundedAveragePrice}
				totalCost={roundedCost}
			/>
			{isOverrangeQuantity && (
				<WarningAlert
					description={t('offsets.textOverMaxQuantity')}
				/>
			)}
			{isMaxSelectedProjects && (
				<WarningAlert
					description={t(
						'offsets.notifications.youCantSelectMoreThan4Projects'
					)}
				/>
			)}
		</Box>
	);
};

export default MeasurabilitiesOffsetSummaryCart;
